import axios from 'axios';
import * as Sentry from '@sentry/react';
import {
  getStorageToken,
  clearStorageSession,
  getApiEntrypoint,
  isStorageTokenExpired,
  getUserUUIDToken,
  getSessionUUIDToken,
  getImpAdminId,
} from 'utils';
import { RouterUrl } from 'routes';
import { RECLAMATION_URL } from './product';

const isAxiosNetworkError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const message = typeof error.message === 'string' ? error.message.toLowerCase() : '';

  return error.code === 'ERR_NETWORK' || message.includes('network error');
};

const HTTP_TOO_MANY_REQUESTS_STATUS = 429;

const isAxiosTooManyRequestsError = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === HTTP_TOO_MANY_REQUESTS_STATUS;
};

const captureAxiosInterceptorError = (error: unknown, extra?: Record<string, unknown>): void => {
  if (axios.isCancel(error) || isAxiosNetworkError(error) || isAxiosTooManyRequestsError(error)) {
    return;
  }

  Sentry.captureException(error, extra ? { extra } : undefined);
};

const isAuthApiRequest = (url?: string): boolean => {
  if (!url) {
    return false;
  }

  return url.includes('/auth/');
};

export interface IDataStatus {
  status_code: 'success' | 'fail' | 'validation_error' | 'not_found' | 'conflict';
  data: any;
}
export interface IHelperResponse {
  data: IDataStatus;
}

export const statusCodeHelperResponse = async (response: IHelperResponse) => {
  const {
    data: { status_code: status, data },
  } = response;

  if (status === 'success') {
    return await Promise.resolve(data);
  }

  if (status === 'fail' || status === 'not_found' || status === 'conflict') {
    const errorObj = {
      status_code: status,
      message: data?.message,
      error_code: data?.error_code,
      data,
    };
    return await Promise.reject(errorObj);
  }

  if (status === 'validation_error') {
    const errors = data.map((item: any) => ({
      message: item.user_message,
      path: item.property_path,
      code: item.error_code,
    }));
    const errorObj = { status_code: status, data: errors };

    return await Promise.reject(errorObj);
  }
};

const axiosApiInstance = axios.create();
const axiosUnguardedInstance = axios.create();

let isSessionTerminationInProgress = false;

const terminateSessionAndRedirect = (): void => {
  if (isSessionTerminationInProgress) {
    return;
  }
  isSessionTerminationInProgress = true;
  clearStorageSession();
  location.href = RouterUrl.Login;
};

/** Hang the caller until navigation — avoids unhandled rejection → Sentry noise. */
const swallowAfterSessionTermination = async (): Promise<never> => {
  return await new Promise(() => undefined);
};

/** If client token exists and is expired — clear session and redirect to login (do not send the request). */
export const assertClientSessionNotExpired = async (): Promise<void> => {
  const token = getStorageToken();
  if (token && isStorageTokenExpired()) {
    terminateSessionAndRedirect();
    await swallowAfterSessionTermination();
  }
};

const buildContextHeaders = () => {
  const userUuidToken = getUserUUIDToken();
  const sessionUuidToken = getSessionUUIDToken();
  const adminId = getImpAdminId();

  return {
    ...(userUuidToken ? { 'Context-User-Uuid': userUuidToken } : {}),
    ...(sessionUuidToken ? { 'Context-Session-Uuid': sessionUuidToken } : {}),
    ...(adminId ? { 'Context-Admin-Id': adminId } : {}),
  };
};

axiosUnguardedInstance.interceptors.request.use(
  (config: any) => {
    const isFile = config.data instanceof File || config.data instanceof Blob;
    const isFormData = config.data instanceof FormData;
    let token = getStorageToken();
    const skipAuthHeader = isAuthApiRequest(config.url);

    if (token && isStorageTokenExpired()) {
      token = null;
    }

    config.headers = {
      ...(!skipAuthHeader && token ? { 'X-AUTH-TOKEN': token } : {}),
      ...buildContextHeaders(),
    };
    if (['post', 'put', 'patch'].includes(config.method)) {
      if (isFormData) {
        delete config.headers['Content-Type'];
      } else if (isFile) {
        config.headers['Content-Type'] = config.data.type || 'application/octet-stream';
      } else {
        config.headers['Content-Type'] = 'application/json';
      }
    }
    config.baseURL = getApiEntrypoint();
    return config;
  },
  async error => {
    captureAxiosInterceptorError(error);
    throw error;
  },
);

axiosApiInstance.interceptors.request.use(
  async (config: any) => {
    let token = getStorageToken();
    const isReclamationRequest = config.url === RECLAMATION_URL;
    const skipAuthHeader = isAuthApiRequest(config.url);

    if (!isReclamationRequest && token && isStorageTokenExpired()) {
      terminateSessionAndRedirect();
      return await swallowAfterSessionTermination();
    }

    if (isReclamationRequest && token && isStorageTokenExpired()) {
      token = null;
    }

    if (skipAuthHeader) {
      token = null;
    }

    const isFile = config.data instanceof File || config.data instanceof Blob;
    const isFormData = config.data instanceof FormData;

    config.headers = {
      ...(config.headers ?? {}),
      ...(token ? { 'X-AUTH-TOKEN': token } : {}),
      ...buildContextHeaders(),
    };

    if (['post', 'put', 'patch'].includes(config.method)) {
      if (isFormData) {
        const contentTypeHeader = config.headers?.['Content-Type'] ?? config.headers?.['content-type'];
        const hasExplicitMultipartFormData = contentTypeHeader === 'multipart/form-data';

        if (hasExplicitMultipartFormData) {
          config.headers['Content-Type'] = 'multipart/form-data';
          delete config.headers['content-type'];
        } else {
          delete config.headers['Content-Type'];
          delete config.headers['content-type'];
        }
      } else if (isFile) {
        config.headers['Content-Type'] = config.data.type || 'application/octet-stream';
      } else {
        config.headers['Content-Type'] = 'application/json';
      }
    }
    config.baseURL = getApiEntrypoint();
    return config;
  },
  async error => {
    captureAxiosInterceptorError(error);
    throw error;
  },
);

axiosApiInstance.interceptors.response.use(
  response => {
    return response;
  },
  async function (error) {
    if (error.response?.status === 401) {
      terminateSessionAndRedirect();
      return await swallowAfterSessionTermination();
    }

    captureAxiosInterceptorError(error, {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
    });

    return await Promise.reject(error);
  },
);

axiosUnguardedInstance.interceptors.response.use(
  response => {
    return response;
  },
  async function (error) {
    if (error.response?.status === 401) {
      terminateSessionAndRedirect();
      return await swallowAfterSessionTermination();
    }

    captureAxiosInterceptorError(error, {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      detail: error?.response?.data?.detail,
    });

    return await Promise.reject(error);
  },
);

export const refreshFetch = () => axiosApiInstance;
export const fetchUnguarded = () => axiosUnguardedInstance;
